import { useState } from "react";

import { api } from "@/lib/api";
import type { LoggedDose, SuggestedDose } from "@/types";

interface Props {
  /** Label of a chemical on this same list that must not meet this one. */
  incompatibleWith?: string;
  suggestion: SuggestedDose;
  readingId: string;
  onLogged: () => void;
}

/**
 * One thing to add, with a one-tap "I did this".
 *
 * Logging from here stores the calculator's `predicted_effect` alongside the
 * dose, which is what lets the next reading check the calculator against
 * reality — the loop that keeps this honest.
 */
export default function SuggestionCard({
  suggestion,
  readingId,
  onLogged,
  incompatibleWith,
}: Props) {
  const [logging, setLogging] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function log() {
    setLogging(true);
    setError(null);
    try {
      await api.post<LoggedDose>("/api/doses", {
        dosed_at: new Date().toISOString(),
        chemical: suggestion.chemical,
        amount: suggestion.amount,
        unit: suggestion.unit,
        source: "calculator",
        predicted_effect: suggestion.predicted_effect,
        reading_id: readingId,
      });
      setDone(true);
      onLogged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not log that dose.");
    } finally {
      setLogging(false);
    }
  }

  const sideEffects = Object.entries(suggestion.side_effects).filter(
    ([, value]) => Number(value) !== 0,
  );

  return (
    <li className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-(--color-ink-muted)">{suggestion.chemical_label}</p>
          <p className="figure mt-0.5 text-2xl">{suggestion.display}</p>
        </div>
        {suggestion.confidence === "estimate" && (
          <span className="unit shrink-0" title="Depends on conditions we can't measure">
            estimate
          </span>
        )}
      </div>

      <p className="mt-2 text-sm">{suggestion.reason}</p>

      {sideEffects.length > 0 && (
        <p className="mt-2 text-xs text-(--color-ink-muted)">
          Also moves{" "}
          {sideEffects
            .map(([param, value]) => {
              const n = Number(value);
              return `${param.toUpperCase()} ${n > 0 ? "+" : ""}${n}`;
            })
            .join(", ")}
          .
        </p>
      )}

      {/* Named partner, at full weight, above the ordinary cautions.
          The banner above the list carries the sequence and the reason; this is
          here so that someone who scrolled straight to the chemical they were
          about to pour still meets the warning. Deliberately duplicated: the
          cost is one line, and the cost of missing it is chlorine gas. */}
      {incompatibleWith && (
        <p className="mt-2 text-xs" style={{ color: "var(--color-status-high)" }}>
          Not at the same time as {incompatibleWith} — see the note above the list.
        </p>
      )}

      {suggestion.caution && (
        <p className="mt-2 text-xs text-(--color-ink-faint)">{suggestion.caution}</p>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-(--color-status-high)">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={log}
        disabled={logging || done}
        className={[
          "btn mt-3 w-full text-sm",
          done ? "btn-quiet text-(--color-status-ok)" : "btn-primary",
        ].join(" ")}
      >
        {done ? "Logged" : logging ? "Logging…" : "I did this"}
      </button>
    </li>
  );
}
